import { cn } from "@/lib/utils";

interface ConexusSymbolProps {
  className?: string;
  variant?: "gradient" | "navy" | "white" | "black" | "blue-gradient";
  size?: number;
}

export const ConexusSymbol = ({ 
  className, 
  variant = "gradient",
  size = 64 
}: ConexusSymbolProps) => {
  const gradientId = `conexus-gradient-${Math.random().toString(36).substr(2, 9)}`;
  const blueGradientId = `conexus-blue-gradient-${Math.random().toString(36).substr(2, 9)}`;
  const navyGradientId = `conexus-navy-gradient-${Math.random().toString(36).substr(2, 9)}`;
  
  const getColor = () => {
    switch (variant) {
      case "navy":
        return `url(#${navyGradientId})`;
      case "white":
        return "white";
      case "black":
        return "#1a1a1a";
      case "blue-gradient":
        return `url(#${blueGradientId})`;
      default:
        return `url(#${gradientId})`;
    }
  };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn("conexus-symbol", className)}
    >
      <defs>
        {/* Gold Gradient - Brand Guidelines: #B54A1A → #FFB133 → #FFB132 */}
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#B54A1A" />
          <stop offset="50%" stopColor="#FFB133" />
          <stop offset="100%" stopColor="#FFB132" />
        </linearGradient>
        {/* Blue Gradient - Alternative per Brand Guidelines */}
        <linearGradient id={blueGradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#1E40AF" />
          <stop offset="50%" stopColor="#3B82F6" />
          <stop offset="100%" stopColor="#60A5FA" />
        </linearGradient>
        {/* Navy Gradient - Brand Guidelines: #0A1F3A → #0C2D4F → #1A4A7A */}
        <linearGradient id={navyGradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0A1F3A" />
          <stop offset="50%" stopColor="#0C2D4F" />
          <stop offset="100%" stopColor="#1A4A7A" />
        </linearGradient>
      </defs>
      
      {/* Compass Star - The Conexus Constellation */}
      <g fill={getColor()}>
        {/* Central star - larger, representing Conexus as the guide */}
        <circle cx="50" cy="50" r="6" />
        
        {/* North point - primary direction */}
        <circle cx="50" cy="12" r="4" />
        <path d="M48 18 L50 50 L52 18 Z" opacity="0.6" />
        
        {/* South point */}
        <circle cx="50" cy="88" r="3.5" />
        <path d="M48 82 L50 50 L52 82 Z" opacity="0.5" />
        
        {/* East point */}
        <circle cx="88" cy="50" r="3.5" />
        <path d="M82 48 L50 50 L82 52 Z" opacity="0.5" />
        
        {/* West point */}
        <circle cx="12" cy="50" r="3.5" />
        <path d="M18 48 L50 50 L18 52 Z" opacity="0.5" />
        
        {/* NE diagonal - pathway stars */}
        <circle cx="77" cy="23" r="2.5" />
        <path d="M74 25 L50 50 L76 22 Z" opacity="0.4" />
        
        {/* NW diagonal */}
        <circle cx="23" cy="23" r="2.5" />
        <path d="M26 25 L50 50 L24 22 Z" opacity="0.4" />
        
        {/* SE diagonal */}
        <circle cx="77" cy="77" r="2.5" />
        <path d="M74 75 L50 50 L76 78 Z" opacity="0.4" />
        
        {/* SW diagonal */}
        <circle cx="23" cy="77" r="2.5" />
        <path d="M26 75 L50 50 L24 78 Z" opacity="0.4" />
        
        {/* Inner accent stars - subtle constellation detail */}
        <circle cx="50" cy="30" r="1.5" opacity="0.7" />
        <circle cx="70" cy="50" r="1.5" opacity="0.7" />
        <circle cx="50" cy="70" r="1.5" opacity="0.7" />
        <circle cx="30" cy="50" r="1.5" opacity="0.7" />
      </g>
    </svg>
  );
};

export default ConexusSymbol;
